import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Archive, Download, Loader2, Pencil, Plus, RotateCcw, Search, Upload, X } from "lucide-react";
import { StatusPill, tokenLabel } from "./ModuleProfile";

const inputCls = "w-full rounded-lg border border-emerald-950/15 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-600";
const selectCls = inputCls;

const emptyDraft = {
  name: "",
  relationship: "supplier",
  gstin: "",
  state: "",
  cpcb_status: "unknown",
  cpcb_registration_number: "",
  epr_role: "unknown",
  msme_category: "unknown",
  verification_date: "",
  verification_source: "",
  evidence_note: "",
  status: "active",
};

const emptySummary = {
  total: 0,
  relationships: { supplier: 0, customer: 0, both: 0 },
  cpcb: { registered: 0, not_registered: 0, unknown: 0, pending_verification: 0 },
  msme: { micro: 0, small: 0, medium: 0, large: 0, unknown: 0 },
  states: [],
};

const statusOptions = [
  ["registered", "Registered"],
  ["not_registered", "Not registered"],
  ["pending_verification", "Pending verification"],
  ["unknown", "Unknown"],
];

const msmeOptions = ["micro", "small", "medium", "large", "unknown"];
const roleOptions = ["producer", "importer", "brand_owner", "pwp", "recycler", "other", "unknown"];
const csvHeader = "name,relationship,gstin,state,cpcb_status,cpcb_registration_number,epr_role,msme_category,verification_date,verification_source,evidence_note";

function SummaryButton({ label, value, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={`rounded-2xl border p-4 text-left transition-all ${active ? "border-emerald-600 bg-emerald-600/10 shadow-sm" : "border-emerald-950/10 bg-white hover:border-emerald-600/40 hover:bg-emerald-900/[0.03]"}`}>
      <div className="font-display text-2xl font-bold text-forest-deep">{value}</div>
      <div className="mt-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-forest-deep/50">{label}</div>
    </button>
  );
}

export default function CounterpartyManager({ api, basePath, canEdit, orgName }) {
  const [payload, setPayload] = useState({ items: [], summary: emptySummary });
  const [filters, setFilters] = useState({ relationship: "", cpcb_status: "", msme_category: "", q: "" });
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
    const suffix = params.toString() ? `?${params.toString()}` : "";
    const data = await api(`${basePath}${suffix}`);
    setPayload({ items: data.items || [], summary: data.summary || emptySummary });
    setLoading(false);
  }, [api, basePath, filters]);

  useEffect(() => {
    load().catch((e) => {
      setLoading(false);
      toast.error(e.message);
    });
  }, [load]);

  const summary = payload.summary || emptySummary;
  const hasFilter = useMemo(() => Object.values(filters).some(Boolean), [filters]);

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: current[key] === value ? "" : value }));
  const setMsmeFilter = (value) =>
    setFilters((current) =>
      current.cpcb_status === "registered" && current.msme_category === value
        ? { ...current, msme_category: "" }
        : { ...current, cpcb_status: "registered", msme_category: value }
    );
  const clearFilters = () => setFilters({ relationship: "", cpcb_status: "", msme_category: "", q: "" });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = editingId ? `${basePath}/${editingId}` : basePath;
      await api(path, { method: editingId ? "PATCH" : "POST", body: JSON.stringify(draft) });
      toast.success(editingId ? "Counterparty updated" : "Counterparty added");
      setDraft(emptyDraft);
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setDraft({ ...emptyDraft, ...item });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const archive = async (item) => {
    try {
      await api(`${basePath}/${item.id}`, { method: "PATCH", body: JSON.stringify({ ...item, status: "archived" }) });
      toast.success("Counterparty archived");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const importFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true);
    setImportResult(null);
    try {
      const csvText = await file.text();
      const result = await api(`${basePath}/import`, { method: "POST", body: JSON.stringify({ csv_text: csvText }) });
      setImportResult(result);
      toast.success(`Import finished: ${result.created} added, ${result.updated} updated`);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      e.target.value = "";
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([`${csvHeader}\n`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "clymaco-counterparty-template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="mt-8" data-testid="counterparty-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-forest-deep">Counterparty compliance board</h2>
          <p className="mt-1 text-sm text-forest-deep/60">Click a number to open that exact list for {orgName}.</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-2 rounded-full border border-emerald-950/15 px-4 py-2 text-sm font-semibold text-forest-deep hover:bg-emerald-900/5" data-testid="network-template-download">
              <Download className="h-4 w-4" /> CSV template
            </button>
            <button type="button" onClick={() => setShowImport((v) => !v)} className="inline-flex items-center gap-2 rounded-full border border-emerald-950/15 px-4 py-2 text-sm font-semibold text-forest-deep hover:bg-emerald-900/5" data-testid="network-import-toggle">
              <Upload className="h-4 w-4" /> Import CSV
            </button>
          </div>
        )}
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="network-summary">
        <SummaryButton label="Total network" value={summary.total} active={!hasFilter} onClick={clearFilters} testid="network-summary-total" />
        <SummaryButton label="Registered" value={summary.cpcb.registered} active={filters.cpcb_status === "registered"} onClick={() => setFilter("cpcb_status", "registered")} testid="network-summary-registered" />
        <SummaryButton label="Not registered" value={summary.cpcb.not_registered} active={filters.cpcb_status === "not_registered"} onClick={() => setFilter("cpcb_status", "not_registered")} testid="network-summary-not-registered" />
        <SummaryButton label="Pending" value={summary.cpcb.pending_verification} active={filters.cpcb_status === "pending_verification"} onClick={() => setFilter("cpcb_status", "pending_verification")} testid="network-summary-pending" />
        <SummaryButton label="Unknown" value={summary.cpcb.unknown} active={filters.cpcb_status === "unknown"} onClick={() => setFilter("cpcb_status", "unknown")} testid="network-summary-unknown" />
      </div>

      <div className="mt-4 rounded-3xl border border-emerald-950/10 bg-white p-4 shadow-sm" data-testid="network-msme-board">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-widest text-forest-deep/50">MSME mix among registered counterparties</div>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5">
          {msmeOptions.map((key) => (
            <SummaryButton key={key} label={tokenLabel(key)} value={summary.msme[key]} active={filters.cpcb_status === "registered" && filters.msme_category === key} onClick={() => setMsmeFilter(key)} testid={`network-msme-${key}`} />
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-950/10 bg-white p-3 shadow-sm" data-testid="network-filters">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-forest-deep/35" />
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Search name, GSTIN, state or registration number" className={`${inputCls} pl-9`} data-testid="network-search" />
        </div>
        <select value={filters.relationship} onChange={(e) => setFilters((f) => ({ ...f, relationship: e.target.value }))} className={`${selectCls} w-40`} data-testid="network-filter-relationship">
          <option value="">All relationships</option>
          <option value="supplier">Suppliers</option>
          <option value="customer">Customers</option>
          <option value="both">Both</option>
        </select>
        <select value={filters.cpcb_status} onChange={(e) => setFilters((f) => ({ ...f, cpcb_status: e.target.value }))} className={`${selectCls} w-48`} data-testid="network-filter-cpcb">
          <option value="">All CPCB statuses</option>
          {statusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        {hasFilter && (
          <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-950/15 px-3 py-2 text-xs font-bold text-forest-deep hover:bg-emerald-900/5" data-testid="network-clear-filters">
            <RotateCcw className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>

      {showImport && canEdit && (
        <div className="mt-4 rounded-2xl border-2 border-dashed border-emerald-950/15 bg-white/70 p-5" data-testid="network-import-panel">
          <h3 className="font-display text-base font-bold text-forest-deep">Import supplier and buyer list</h3>
          <p className="mt-1 text-sm text-forest-deep/60">Use the template columns. Existing GSTIN or name rows are updated instead of duplicated.</p>
          <input type="file" accept=".csv,text/csv" onChange={importFile} disabled={busy} className="mt-4 block w-full text-sm text-forest-deep file:mr-4 file:rounded-full file:border-0 file:bg-forest-primary file:px-4 file:py-2 file:text-sm file:font-bold file:text-white" data-testid="network-import-file" />
          {importResult && (
            <div className="mt-3 rounded-xl bg-emerald-900/5 px-4 py-3 text-sm text-forest-deep" data-testid="network-import-result">
              {importResult.created} added · {importResult.updated} updated · {importResult.errors.length} row errors
              {importResult.errors.length > 0 && <div className="mt-1 text-xs text-red-700">{importResult.errors.map((e) => `Row ${e.row}: ${e.error}`).join(" | ")}</div>}
            </div>
          )}
        </div>
      )}

      {canEdit && (
        <form onSubmit={submit} className="mt-5 rounded-3xl border border-emerald-950/10 bg-white p-5 shadow-sm" data-testid="network-form">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-display text-base font-bold text-forest-deep">{editingId ? "Edit counterparty" : "Add counterparty"}</h3>
            {editingId && (
              <button type="button" onClick={() => { setEditingId(null); setDraft(emptyDraft); }} className="inline-flex items-center gap-1 text-xs font-bold text-forest-deep/60 hover:text-forest-deep" data-testid="network-cancel-edit">
                <X className="h-3.5 w-3.5" /> Cancel edit
              </button>
            )}
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <input required placeholder="Counterparty legal or trade name" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} className={inputCls} data-testid="network-input-name" />
            <select value={draft.relationship} onChange={(e) => setDraft((d) => ({ ...d, relationship: e.target.value }))} className={selectCls} data-testid="network-input-relationship">
              <option value="supplier">They sell to us</option>
              <option value="customer">They buy from us</option>
              <option value="both">Both</option>
            </select>
            <input placeholder="GSTIN" value={draft.gstin} onChange={(e) => setDraft((d) => ({ ...d, gstin: e.target.value }))} className={inputCls} data-testid="network-input-gstin" />
            <input placeholder="State" value={draft.state} onChange={(e) => setDraft((d) => ({ ...d, state: e.target.value }))} className={inputCls} data-testid="network-input-state" />
            <select value={draft.cpcb_status} onChange={(e) => setDraft((d) => ({ ...d, cpcb_status: e.target.value }))} className={selectCls} data-testid="network-input-cpcb-status">
              {statusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <input placeholder="CPCB registration number" value={draft.cpcb_registration_number} onChange={(e) => setDraft((d) => ({ ...d, cpcb_registration_number: e.target.value }))} className={inputCls} data-testid="network-input-registration" />
            <select value={draft.epr_role} onChange={(e) => setDraft((d) => ({ ...d, epr_role: e.target.value }))} className={selectCls} data-testid="network-input-epr-role">
              {roleOptions.map((value) => <option key={value} value={value}>{tokenLabel(value)}</option>)}
            </select>
            <select value={draft.msme_category} onChange={(e) => setDraft((d) => ({ ...d, msme_category: e.target.value }))} className={selectCls} data-testid="network-input-msme">
              {msmeOptions.map((value) => <option key={value} value={value}>{tokenLabel(value)}</option>)}
            </select>
            <input type="date" value={draft.verification_date} onChange={(e) => setDraft((d) => ({ ...d, verification_date: e.target.value }))} className={inputCls} data-testid="network-input-verification-date" />
            <input placeholder="Verification source" value={draft.verification_source} onChange={(e) => setDraft((d) => ({ ...d, verification_source: e.target.value }))} className={inputCls} data-testid="network-input-source" />
            <input placeholder="Evidence note" value={draft.evidence_note} onChange={(e) => setDraft((d) => ({ ...d, evidence_note: e.target.value }))} className={`${inputCls} lg:col-span-2`} data-testid="network-input-note" />
          </div>
          <button type="submit" disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded-full bg-forest-primary px-5 py-2.5 font-display text-sm font-bold text-white hover:bg-forest-rich disabled:opacity-60" data-testid="network-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {editingId ? "Save changes" : "Add counterparty"}
          </button>
        </form>
      )}

      <div className="mt-5 overflow-hidden rounded-3xl border border-emerald-950/10 bg-white shadow-sm" data-testid="network-table-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-sm" data-testid="network-table">
            <thead className="bg-emerald-900/[0.04] font-mono text-[10px] uppercase tracking-widest text-forest-deep/50">
              <tr>
                <th className="px-4 py-3">Counterparty</th>
                <th className="px-4 py-3">Relationship</th>
                <th className="px-4 py-3">CPCB</th>
                <th className="px-4 py-3">MSME</th>
                <th className="px-4 py-3">State / GSTIN</th>
                <th className="px-4 py-3">Verification</th>
                {canEdit && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {payload.items.map((item) => (
                <tr key={item.id} className="border-t border-emerald-950/5 align-top" data-testid={`network-row-${item.id}`}>
                  <td className="px-4 py-4">
                    <div className="font-bold text-forest-deep" data-testid={`network-name-${item.id}`}>{item.name}</div>
                    <div className="mt-1 text-xs text-forest-deep/50">{tokenLabel(item.epr_role)}{item.cpcb_registration_number ? ` · ${item.cpcb_registration_number}` : ""}</div>
                  </td>
                  <td className="px-4 py-4 text-forest-deep/70">{item.relationship === "supplier" ? "They sell to us" : item.relationship === "customer" ? "They buy from us" : "Both"}</td>
                  <td className="px-4 py-4"><StatusPill value={item.cpcb_status} testid={`network-cpcb-${item.id}`} /></td>
                  <td className="px-4 py-4 text-forest-deep/70" data-testid={`network-msme-cell-${item.id}`}>{tokenLabel(item.msme_category)}</td>
                  <td className="px-4 py-4 text-forest-deep/70">
                    <div>{item.state || "Not provided"}</div>
                    <div className="mt-1 font-mono text-[11px] text-forest-deep/45">{item.gstin || "GSTIN not added"}</div>
                  </td>
                  <td className="px-4 py-4 text-xs text-forest-deep/60">
                    <div>{item.verification_date || "Date not added"}</div>
                    {item.verification_source && <div className="mt-1">{item.verification_source}</div>}
                    {item.evidence_note && <div className="mt-1 max-w-[220px] truncate" title={item.evidence_note}>{item.evidence_note}</div>}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-1.5">
                        <button type="button" onClick={() => startEdit(item)} aria-label={`Edit ${item.name}`} className="rounded-full border border-emerald-950/15 p-2 text-forest-deep hover:bg-emerald-900/5" data-testid={`network-edit-${item.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => archive(item)} aria-label={`Archive ${item.name}`} className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50" data-testid={`network-archive-${item.id}`}>
                          <Archive className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && payload.items.length === 0 && (
          <div className="px-6 py-12 text-center" data-testid="network-empty">
            <p className="font-display text-lg font-bold text-forest-deep">No counterparties in this view</p>
            <p className="mt-1 text-sm text-forest-deep/55">{hasFilter ? "Clear the filters or add a matching record." : "Add the first supplier or buyer to start the compliance board."}</p>
          </div>
        )}
        {loading && <div className="px-6 py-10 text-center text-sm text-forest-deep/55" data-testid="network-loading">Loading network...</div>}
      </div>

      {!canEdit && (
        <p className="mt-4 rounded-2xl bg-emerald-900/5 px-4 py-3 text-sm text-forest-deep/65" data-testid="network-readonly-note">
          You have read-only access. The client admin or Clymaco staff can add and update counterparties.
        </p>
      )}
    </section>
  );
}
